import { FC } from 'react';

import { GiftDeliveryForAdmin } from '@shared/api/giftDelivery/giftDelivery.interface';
import { fileBucketUrl } from '@shared/utils/fileUtils';

import { typeBadgeStyle, typeLabel } from '../utils/delivery.constants';
import { ImageModalState } from '../utils/delivery.types';
import { formatDateTime, getItemName } from '../utils/delivery.utils';

interface Props {
  deliveries: GiftDeliveryForAdmin[];
  onOpenImage: (modal: ImageModalState) => void;
  onOpenEdit: (delivery: GiftDeliveryForAdmin) => void;
}

/**
 * 배송완료 항목 테이블 (오늘 배송완료 섹션 / 배송완료 필터 전체 조회 공용).
 * 완료시각·완료사진·수정 기능을 포함한다.
 */
export const DeliveredDeliveryTable: FC<Props> = ({
  deliveries,
  onOpenImage,
  onOpenEdit,
}) => {
  return (
    <div className="overflow-x-auto rounded-lg bg-white shadow">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-green-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
              코드
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
              상품
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
              타입
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
              수량
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
              수령인
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
              완료시각
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
              배송지
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
              완료사진
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
              관리
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {deliveries.map((delivery) => {
            const deliveredImg = delivery.shipping?.deliveredImage;
            const imgUrl =
              deliveredImg && typeof deliveredImg === 'object'
                ? fileBucketUrl(deliveredImg.url, 'w400')
                : null;
            return (
              <tr key={delivery._id} className="hover:bg-gray-50">
                <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-gray-700">
                  {delivery.fullCode || delivery.code || '-'}
                </td>
                <td className="px-4 py-2 text-xs text-gray-900">
                  {getItemName(delivery)}
                </td>
                <td className="px-4 py-2">
                  <span
                    className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      typeBadgeStyle[delivery.type] || 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {typeLabel[delivery.type] || delivery.type}
                  </span>
                </td>
                <td className="px-4 py-2 text-xs text-gray-500">{delivery.quantity}</td>
                <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-700">
                  {delivery.recipient?.name || '-'}
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-600">
                  {formatDateTime(delivery.shipping?.deliveredAt)}
                </td>
                <td
                  className="max-w-[200px] truncate px-4 py-2 text-xs text-gray-600"
                  title={delivery.recipient?.address || ''}
                >
                  {delivery.recipient?.address || '-'}
                </td>
                <td className="px-4 py-2">
                  {imgUrl ? (
                    <button
                      onClick={() =>
                        onOpenImage({
                          url: imgUrl,
                          itemName: getItemName(delivery),
                          address: delivery.recipient?.address || '-',
                          deliveredAt: formatDateTime(delivery.shipping?.deliveredAt),
                        })
                      }
                      className="rounded bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-200"
                    >
                      사진보기
                    </button>
                  ) : (
                    <span className="text-xs text-gray-400">-</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  <button
                    onClick={() => onOpenEdit(delivery)}
                    className="rounded bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-200"
                  >
                    수정
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
